import { useEffect, useState } from "react"
import { router } from "@inertiajs/react"
import { AlertTriangle, CheckCircle2, XCircle } from "lucide-react"
import { SeoHead } from "@/components/SeoHead"
import { MarketingLayout } from "@/components/marketing/MarketingLayout"
import { Section, Eyebrow } from "@/components/marketing/sections/Section"
import { Button } from "@/components/ui/button"
import { GradeBadge } from "@/components/ui/grade-badge"
import { FindingCard } from "@/components/ui/finding-card"
import { FindingGroup } from "@/components/ui/finding-group"

type Check = {
  key: string
  label: string
  status: "pass" | "warn" | "fail"
  finding: string
  detail?: string
}

// The ONLY thing ever rendered as a rank is `label` — it's a verbatim string
// built server-side from position + result_count together ("#7 of 97", "not
// checked", "not in the 100 results Apple returned", ...). `result_count` is
// deliberately typed non-optional: consumers should never compose their own
// rank string from `position` alone, because a bare "#1" is meaningless
// without knowing how deep the result pool was.
type Rank = {
  keyword: string
  label: string
  position: number | null
  result_count: number
  thin: boolean
  checked: boolean
  winner?: { name: string; author: string; title_contains_keyword: boolean }
}

type Method = {
  checked: string[]
  not_checked: string[]
}

type Apple = {
  state: "found" | "duplicate" | "title_collision" | "not_indexed" | "unknown"
  collection_id: number | null
  identity_match: string | null
  duplicate_ids: number[] | null
}

type Results = {
  fetched_at: string
  feed_url: string | null
  final_url: string | null
  feed_truncated: boolean | null
  apple: Apple
  keyword_source: string | null
  keywords_extra: string[]
  ranks: Rank[]
  method: Method
  checks: Check[]
}

type Props = {
  status: "pending" | "running" | "complete" | "failed"
  grade: string | null
  score: number | null
  show_title: string | null
  input_url: string
  email: string
  results: Results | null
  created_at: string
}

const STAGES = [
  "Reading your feed",
  "Finding your show on Apple",
  "Deriving your keywords",
  "Checking where you rank",
]

export default function PodcastAuditResult({ status, grade, score, show_title, input_url, results }: Props) {
  const inFlight = status === "pending" || status === "running"

  // Polling gives up after 3 minutes — comfortably past the runner's 75s
  // deadline plus queue wait. Giving up has to be visible: silently stopping
  // left the page insisting "Running the audit" forever, which reads as a hang.
  const [timedOut, setTimedOut] = useState(false)

  useEffect(() => {
    if (!inFlight) {
      setTimedOut(false) // a late result should clear a previous timeout
      return
    }
    const interval = setInterval(() => {
      router.reload({ only: ["status", "grade", "score", "show_title", "results"] })
    }, 3000)
    const bail = setTimeout(() => {
      clearInterval(interval)
      setTimedOut(true)
    }, 180_000)
    return () => {
      clearInterval(interval)
      clearTimeout(bail)
    }
  }, [inFlight])

  const displayName = show_title || input_url

  return (
    <MarketingLayout>
      <SeoHead
        title="Your Podcast Audit"
        description={`Podcast discoverability audit report for ${displayName}`}
        noindex
      />

      <Section containerClassName="max-w-3xl">
        <Eyebrow>FullCast · Podcast Discoverability Audit</Eyebrow>

        {/* aria-live so a screen-reader user is told the audit finished, rather
            than having to notice the page quietly rewrote itself. polite, not
            assertive — it shouldn't interrupt whatever they're reading. */}
        <div role="status" aria-live="polite">
          {inFlight && !timedOut && <Running name={displayName} />}
          {inFlight && timedOut && <TimedOut name={displayName} />}
          {status === "failed" && <Failed name={displayName} />}
          {status === "complete" && results && (
            <Complete name={displayName} grade={grade!} score={score!} results={results} />
          )}
        </div>
      </Section>
    </MarketingLayout>
  )
}

function Running({ name }: { name: string }) {
  return (
    <>
      <h1 className="display-1 mt-4">Auditing {name}…</h1>
      <p className="mt-5 text-lg text-ink-body">
        This one's slower than a website audit — we're deriving keywords and
        checking Apple Podcasts search one at a time. Usually done in well
        under a minute. Leave this page open; it updates automatically.
      </p>
      <div className="mt-10 rounded-2xl border border-hairline bg-surface p-6 sm:p-8">
        <div className="flex items-center gap-3">
          <span className="inline-block h-3 w-3 animate-pulse rounded-full bg-accent" aria-hidden="true" />
          <span className="font-medium text-ink-display">Running the audit</span>
        </div>
        <ol className="mt-4 space-y-2 text-sm text-ink-muted">
          {STAGES.map((stage, i) => (
            <li key={stage}>
              {i + 1}. {stage}
            </li>
          ))}
        </ol>
        <p className="mt-6 text-sm text-ink-muted">
          Taking longer than expected? Leave this page open — or email{" "}
          <a href="mailto:hello@fullcast.co">hello@fullcast.co</a> and we'll send
          the report when it's done.
        </p>
      </div>
    </>
  )
}

// Polling gave up. The audit may well still finish — the job outlives this
// page — so this says what's actually true rather than declaring failure.
function TimedOut({ name }: { name: string }) {
  return (
    <>
      <h1 className="display-1 mt-4">Still working on {name}</h1>
      <p className="mt-5 text-lg text-ink-body">
        This is taking longer than usual, so we've stopped refreshing the page.
        The audit is still running in the background and you'll get the report
        by email when it lands.
      </p>
      <div className="mt-10 rounded-2xl border border-hairline bg-surface p-6 sm:p-8">
        <p className="text-ink-body">
          You can reload this page to check again, or email{" "}
          <a href="mailto:hello@fullcast.co">hello@fullcast.co</a> if you don't
          hear from us — a feed that's slow to respond is itself worth knowing
          about, and we'll look into it.
        </p>
        <div className="mt-6">
          <button
            type="button"
            onClick={() => router.reload()}
            className="rounded-xl bg-ink-display px-5 py-2.5 text-page"
          >
            Check again
          </button>
        </div>
      </div>
    </>
  )
}

function Failed({ name }: { name: string }) {
  return (
    <>
      <h1 className="display-1 mt-4">That one needs a human.</h1>
      <p className="mt-5 text-lg text-ink-body">
        Something about {name} tripped up the automated audit — could be a
        malformed feed, could be Apple's API hiccuping. Sorry about that.
        Email us and we'll take a look by hand, free.
      </p>
      <div className="mt-8">
        <Button asChild size="lg">
          <a href={`mailto:hello@fullcast.co?subject=Manual%20podcast%20audit%20request&body=Show:%20${encodeURIComponent(name)}`}>
            Request a manual audit
          </a>
        </Button>
      </div>
    </>
  )
}

function Complete({ name, grade, score, results }: { name: string; grade: string; score: number; results: Results }) {
  const fails = results.checks.filter((c) => c.status === "fail")
  const warns = results.checks.filter((c) => c.status === "warn")
  const passes = results.checks.filter((c) => c.status === "pass")
  const appleUnresolved = results.apple.state === "not_indexed" || results.apple.state === "unknown"

  return (
    <>
      <div className="mt-4 flex items-center gap-6">
        <GradeBadge grade={grade} size="lg" />
        <div>
          <h1 className="display-2">{name}</h1>
          <p className="mt-1 text-ink-muted">Scored {score}/100</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {fails.length > 0 && (
              <SummaryChip icon={XCircle} className="bg-danger-faded text-danger-display">
                {fails.length} {fails.length === 1 ? "issue" : "issues"}
              </SummaryChip>
            )}
            {warns.length > 0 && (
              <SummaryChip icon={AlertTriangle} className="bg-signal-faded text-signal-display">
                {warns.length} {warns.length === 1 ? "warning" : "warnings"}
              </SummaryChip>
            )}
            <SummaryChip icon={CheckCircle2} className="bg-success-faded text-success-display">
              {passes.length} passed
            </SummaryChip>
          </div>
        </div>
      </div>

      {fails.length > 0 && (
        <FindingGroup status="fail" title={`What's hurting you (${fails.length})`}>
          {fails.map((check) => (
            <FindingCard key={check.key} status={check.status} title={check.label} body={check.finding} detail={check.detail} />
          ))}
        </FindingGroup>
      )}
      {warns.length > 0 && (
        <FindingGroup status="warn" title={`Worth fixing (${warns.length})`}>
          {warns.map((check) => (
            <FindingCard key={check.key} status={check.status} title={check.label} body={check.finding} detail={check.detail} />
          ))}
        </FindingGroup>
      )}
      {passes.length > 0 && (
        <FindingGroup status="pass" title={`What's working (${passes.length})`}>
          {passes.map((check) => (
            <FindingCard key={check.key} status={check.status} title={check.label} body={check.finding} detail={check.detail} />
          ))}
        </FindingGroup>
      )}

      <div className="mt-10">
        {appleUnresolved ? (
          <NotIndexedPanel state={results.apple.state} name={name} />
        ) : (
          results.ranks.length > 0 && (
            <>
              <h2>Where you rank on Apple Podcasts</h2>
              <div className="mt-4">
                <RankTable ranks={results.ranks} />
              </div>
            </>
          )
        )}
      </div>

      <MethodNote method={results.method} />

      <div className="mt-12 rounded-2xl border border-hairline bg-surface p-6 sm:p-8">
        <h2 className="display-2">Want help fixing this?</h2>
        <p className="mt-3 text-ink-body">
          I produce podcasts for a living — this audit is the same lens I'd
          apply to a client's show. Tell me what's going on and I'll reply
          with a straight read on what's worth doing first. No obligation
          either way — this report is yours to use with anyone.
        </p>
        <div className="mt-6">
          <Button asChild size="lg">
            <a href={`mailto:hello@fullcast.co?subject=Podcast%20audit%20follow-up&body=My%20show:%20${encodeURIComponent(name)}%20(grade%20${grade})`}>
              Email me about this report
            </a>
          </Button>
        </div>
      </div>
    </>
  )
}

function NotIndexedPanel({ state, name }: { state: Apple["state"]; name: string }) {
  const heading =
    state === "not_indexed" ? "Apple's index can't see your show" : "We couldn't confirm your Apple Podcasts listing"

  const body =
    state === "not_indexed"
      ? `We searched Apple Podcasts for your show's own exact title — "${name}" — and it returned nothing. That's not a ranking problem, it's a visibility problem: until Apple's index can see your show at all, no keyword strategy matters, because you're invisible to search in the first place. Everything else in this report is secondary to fixing this.`
      : "The verification search itself failed, so we can't say whether Apple's index can currently see your show. Treat the rest of this report as provisional until this is confirmed — it's worth re-running the audit or checking your listing directly."

  return (
    <div className="rounded-2xl border-2 border-danger bg-danger-faded p-6 sm:p-8">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-1 h-6 w-6 shrink-0 text-danger-display" aria-hidden="true" />
        <div>
          <h2>{heading}</h2>
          <p className="mt-2 text-ink-body">{body}</p>
        </div>
      </div>
    </div>
  )
}

function RankTable({ ranks }: { ranks: Rank[] }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-hairline">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-hairline bg-surface text-left">
            <th className="px-4 py-3 font-medium text-ink-muted">Keyword</th>
            <th className="px-4 py-3 font-medium text-ink-muted">Your rank</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-hairline">
          {ranks.map((rank) => (
            <tr key={rank.keyword}>
              <td className="px-4 py-3 font-medium text-ink-display">{rank.keyword}</td>
              <td className="px-4 py-3 text-ink-body">
                {/* Render `label` verbatim — never compose a rank string from `position`. */}
                {rank.label}
                {rank.thin && <span className="ml-2 text-xs text-ink-muted">(small pool)</span>}
                {rank.winner && (
                  <p className="mt-1 text-xs text-ink-muted">
                    Top result: <strong>{rank.winner.name}</strong> by {rank.winner.author}
                    {rank.winner.title_contains_keyword && <> — "{rank.keyword}" is in their show title</>}
                  </p>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function MethodNote({ method }: { method: Method }) {
  return (
    <div className="mt-10 rounded-xl border border-hairline bg-surface p-5 sm:p-6">
      <h3>How this audit works</h3>
      <p className="mt-2 text-sm text-ink-body">
        <strong>We checked:</strong> {method.checked.join(", ")}.
      </p>
      <p className="mt-1 text-sm text-ink-body">
        <strong>We did not check:</strong> {method.not_checked.join("; ")}.
      </p>
      <p className="mt-2 text-sm text-ink-muted">
        We'd rather tell you what this audit can't see than pretend it's the whole picture.
      </p>
    </div>
  )
}

function SummaryChip({
  icon: Icon,
  className,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>
  className: string
  children: React.ReactNode
}) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold ${className}`}>
      <Icon className="h-4 w-4" />
      {children}
    </span>
  )
}
